import * as React from 'react'

import { create } from '@bufbuild/protobuf'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  Controller,
  SubmitHandler,
  useFieldArray,
  useForm,
  useWatch,
} from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import {
  Box,
  Field,
  Flex,
  Input,
  NativeSelect,
  Stack,
  Text,
} from '@chakra-ui/react'

import {
  OutlineDangerButton,
  PrimaryButton,
  Switch,
  TertiaryButton,
  Tooltip,
} from '@enechain/ecloud-designsystem'
import { FaCircleQuestionRegular } from '@enechain/ecloud-icons'

import { SaveBillingEmailsRequestSchema } from '~/gen/proto/bff/v1/admin_pb'
import { BillingEmail, BillingEmailSchema } from '~/gen/proto/bff/v1/model_pb'
import { useLoginUser } from '~/hooks/useLoginUser'
import BillingEmailEditFormItem from '~/pages/Billing/components/BillingEmailEditForm/BillingEmailEditFormItem'
import {
  BillingEmailEditSchema,
  billingEmailEditSchema,
} from '~/pages/Billing/components/BillingEmailEditForm/billingEmailEditSchema'
import { useBillingEmailEnabledProducts } from '~/pages/Billing/hooks/useBillingEmailEnabledProducts'
import { useBillingEmailsMutation } from '~/pages/Billing/hooks/useBillingEmailsMutation'
import { useGetCompanyAllowedMailDomains } from '~/pages/Billing/hooks/useGetCompanyAllowedMailDomains'

type Props = {
  billingEmails: BillingEmail[]
  onEditSuccess: () => void
  onCancel: () => void
}

const emailsToFields = (emails: string[]): Array<{ value: string }> =>
  emails.length > 0 ? emails.map((value) => ({ value })) : [{ value: '' }]

const BillingEmailEditForm: React.FC<Props> = ({
  billingEmails: initialBillingEmails,
  onEditSuccess,
  onCancel,
}) => {
  const { t } = useTranslation()
  const me = useLoginUser()
  const allowedMailDomain = useGetCompanyAllowedMailDomains(me.companyId)
  const enabledProducts = useBillingEmailEnabledProducts()

  // 全体送付先ブロックは常に先頭に1つ表示する。
  const generalInitial = initialBillingEmails.find((e) => e.isGeneral)
  const productInitials = initialBillingEmails.filter((e) => !e.isGeneral)

  const {
    register,
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<BillingEmailEditSchema>({
    defaultValues: {
      billingEmails: [
        {
          productId: 0,
          isGeneral: true,
          useDisplayName: false,
          displayName: '',
          emails: emailsToFields(generalInitial?.emails ?? []),
        },
        ...productInitials.map((e) => ({
          productId: e.productId ?? 0,
          isGeneral: false,
          useDisplayName: !!e.displayName,
          displayName: e.displayName ?? '',
          emails: emailsToFields(e.emails),
        })),
      ],
    },
    resolver: zodResolver(
      billingEmailEditSchema(allowedMailDomain.domains ?? []),
    ),
  })

  const { fields, append, remove } = useFieldArray({
    name: 'billingEmails',
    control,
  })
  const watchedBillingEmails = useWatch({ control, name: 'billingEmails' })
  // 既に宛名を設定しているプロダクト設定があるなら、追加分も同様に設定したい可能性が高いためトグルをオンにする
  const hasCustomDisplayName = watchedBillingEmails.some(
    (e) => !e.isGeneral && !!e.displayName,
  )
  const { mutate, isPending } = useBillingEmailsMutation(me.organizationId)

  const onSubmit: SubmitHandler<BillingEmailEditSchema> = (data) => {
    const submittedProductIds = new Set(
      data.billingEmails.map((e) => e.productId),
    )
    // 入力された送付先。
    const billingEmails = data.billingEmails.map((e) =>
      create(BillingEmailSchema, {
        organizationId: me.organizationId,
        productId: e.productId,
        isGeneral: e.isGeneral,
        displayName: e.useDisplayName ? (e.displayName ?? '') : '',
        emails: e.emails.map((email) => email.value),
      }),
    )
    // 画面から削除されたプロダクト個別送付先は、空配列で送って削除する。
    const deletedBillingEmails = productInitials
      .filter((e) => !submittedProductIds.has(e.productId ?? 0))
      .map((e) =>
        create(BillingEmailSchema, {
          organizationId: me.organizationId,
          productId: e.productId,
          isGeneral: false,
          displayName: '',
          emails: [],
        }),
      )

    mutate(
      create(SaveBillingEmailsRequestSchema, {
        billingEmails: [...billingEmails, ...deletedBillingEmails],
      }),
      {
        onSuccess: () => {
          onEditSuccess()
        },
      },
    )
  }

  const productOptions = Array.from(enabledProducts.entries())
  const defaultProductId = productOptions[0]?.[0] ?? 0

  return (
    <Flex
      flexDirection="column"
      gap="3"
      maxWidth="1280px"
      margin="auto"
      padding="2"
    >
      <Text textStyle="headerMd" color="Text.highEmphasis" textAlign="left">
        {t('pages.billing.label.subtitle')}
      </Text>

      {fields.map((field, index) => {
        const isGeneral = field.isGeneral
        return (
          <Flex
            key={field.id}
            flexDirection="column"
            gap="3"
            padding="4"
            borderWidth="1px"
            borderColor="Border.midEmphasis"
            borderRadius="8px"
          >
            {isGeneral ? (
              <>
                <Text textStyle="headerMd">
                  {t('pages.billing.label.general')}
                </Text>
                <Text>
                  {t('pages.billing.description.billing_email_general_notice')}
                </Text>
                <Flex alignItems="center" gap="1">
                  <Text textStyle="headerSm">
                    {t('pages.billing.label.billing_email')}
                  </Text>
                  <Tooltip label={t('pages.billing.message.billing_email')}>
                    <FaCircleQuestionRegular boxSize="16px" />
                  </Tooltip>
                </Flex>
                <Text textStyle="textSm" color="Text.lowEmphasis">
                  {t('pages.billing.description.billing_email_max_count')}
                </Text>
              </>
            ) : (
              <Flex flexDirection="column" gap="4">
                <Flex alignItems="center" justifyContent="space-between">
                  <Text textStyle="headerMd">
                    {t('pages.billing.label.product_setting')}
                  </Text>
                  <OutlineDangerButton
                    width="200px"
                    onClick={() => remove(index)}
                  >
                    {t('pages.billing.action.delete_product_setting')}
                  </OutlineDangerButton>
                </Flex>
                <Box width="50%">
                  <Field.Root
                    invalid={!!errors.billingEmails?.[index]?.productId}
                  >
                    <NativeSelect.Root>
                      <NativeSelect.Field
                        {...register(
                          `billingEmails.${index}.productId` as const,
                          {
                            valueAsNumber: true,
                          },
                        )}
                        onChange={(e) => {
                          setValue(
                            `billingEmails.${index}.productId`,
                            Number(e.target.value),
                            { shouldValidate: true },
                          )
                        }}
                      >
                        {productOptions.map(([id, name]) => (
                          <option key={id} value={id}>
                            {name}
                          </option>
                        ))}
                      </NativeSelect.Field>
                      <NativeSelect.Indicator />
                    </NativeSelect.Root>
                    <Field.ErrorText>
                      {errors.billingEmails?.[index]?.productId?.message}
                    </Field.ErrorText>
                  </Field.Root>
                </Box>
                <>
                  <Flex alignItems="center" gap="2">
                    <Controller
                      control={control}
                      name={`billingEmails.${index}.useDisplayName` as const}
                      render={({ field: toggle }) => (
                        <Switch
                          checked={toggle.value}
                          onCheckedChange={({ checked }) => {
                            toggle.onChange(checked)
                            if (!checked) {
                              setValue(
                                `billingEmails.${index}.displayName`,
                                '',
                                { shouldValidate: true },
                              )
                            }
                          }}
                        />
                      )}
                    />
                    <Text textStyle="textMd">
                      {t('pages.billing.label.use_display_name')}
                    </Text>
                    <Tooltip label={t('pages.billing.message.display_name')}>
                      <FaCircleQuestionRegular boxSize="16px" />
                    </Tooltip>
                  </Flex>
                  {watchedBillingEmails?.[index]?.useDisplayName && (
                    <Box width="50%">
                      <Field.Root
                        invalid={!!errors.billingEmails?.[index]?.displayName}
                      >
                        <Text textStyle="headerSm" marginBottom="1">
                          {t('pages.billing.label.display_name')}
                        </Text>
                        <Input
                          {...register(
                            `billingEmails.${index}.displayName` as const,
                          )}
                        />
                        <Field.ErrorText>
                          {errors.billingEmails?.[index]?.displayName?.message}
                        </Field.ErrorText>
                      </Field.Root>
                    </Box>
                  )}
                </>
                <Stack>
                  <Text textStyle="headerSm">
                    {t('pages.billing.label.billing_email')}
                  </Text>
                  <Text textStyle="textSm" color="Text.lowEmphasis">
                    {t(
                      'pages.billing.description.billing_email_product_max_count',
                    )}
                  </Text>
                </Stack>
              </Flex>
            )}
            <BillingEmailEditFormItem
              register={register}
              control={control}
              errors={errors}
              index={index}
              isGeneral={isGeneral}
              onRemoveBillingEmail={() => remove(index)}
            />
          </Flex>
        )
      })}

      {productOptions.length > 0 && (
        <Flex justifyContent="center">
          <TertiaryButton
            width="220px"
            onClick={() =>
              append({
                productId: defaultProductId,
                isGeneral: false,
                useDisplayName: hasCustomDisplayName,
                displayName: '',
                emails: [{ value: '' }],
              })
            }
          >
            {t('pages.billing.action.add_product_setting')}
          </TertiaryButton>
        </Flex>
      )}

      <Flex justifyContent="flex-end">
        <Stack direction="row" gap="4">
          <TertiaryButton onClick={onCancel}>
            {t('action.cancel')}
          </TertiaryButton>
          <PrimaryButton onClick={handleSubmit(onSubmit)} loading={isPending}>
            {t('action.save')}
          </PrimaryButton>
        </Stack>
      </Flex>
    </Flex>
  )
}

export default BillingEmailEditForm
